import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { DateTime } from 'luxon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzTableModule } from 'ng-zorro-antd/table';
import { take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ToLocaleFormat } from '../../basic/pipes/to-locale-format.pipe';
import { TruncatePipe } from '../../basic/pipes/truncate.pipe';
import { Board } from '../../core/board/board';
import { ObjectionType } from '../../core/objection/objection';
import { ObjectionService } from '../../core/objection/objection.service';
import { SessionMessage } from '../../core/session-message/session-message';
import { SessionMessageService } from '../../core/session-message/session-message.service';
import { BoardIconComponent } from '../../domain/board/board-icon/board-icon.component';
import { NewEditProductComponent } from '../../domain/product/new-edit-product/new-edit-product.component';
import { BasicPageComponent } from '../_shared/basic-page/basic-page.component';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzInputModule,
    NzIconModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzPopconfirmModule,

    TruncatePipe,
    ToLocaleFormat,

    MatTooltip,

    BasicPageComponent,
    NewEditProductComponent,
    BoardIconComponent,
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsComponent {
  sessionMessageList = signal<SessionMessage[]>([]);

  allObjectionList = toSignal(this.objectionService.itemList$(), { initialValue: [] });

  startTime = DateTime.now().minus({ weeks: 4 });
  endTime = DateTime.now();

  constructor(
    private objectionService: ObjectionService,
    private sessionMessageService: SessionMessageService
  ) {
    const startDate = DateTime.now().minus({ weeks: 4 });
    const endDate = DateTime.now();

    this.sessionMessageService
      .getSessionMessageListByStartEndTime$(startDate, endDate)
      .pipe(take(1))
      .subscribe((sessionMessageList) => {
        this.sessionMessageList.set(sessionMessageList);
      });
  }

  topFailedFactsBasedObjectionList = computed(() => {
    const sessionMessageList = this.sessionMessageList();

    return this.getObjectionList(sessionMessageList, ObjectionType.fact);
  });

  topFailedSoftSkillsObjectionList = computed(() => {
    const sessionMessageList = this.sessionMessageList();

    return this.getObjectionList(sessionMessageList, ObjectionType.emotional);
  });

  onSessionReportClick(id: string) {
    window.open(`${environment.domain}/simulated-meetings/${id}`, '_blank');
  }

  private getObjectionList(sessionMessageList: SessionMessage[], objectionType: ObjectionType) {
    const filteredSessionMessageList = sessionMessageList.filter(
      (c) => c.subData.objectionScore && c.subData.objectionScore <= 3
    );

    const objectionIdAndMemberSet = new Map<string, Set<Board>>();
    const objectionIdAndMessageList = new Map<string, SessionMessage[]>();

    const sessionIdSet = new Set<string>();

    filteredSessionMessageList.forEach((sessionMessage) => {
      if (!objectionIdAndMemberSet.has(sessionMessage.subData.objectionId!)) {
        objectionIdAndMemberSet.set(sessionMessage.subData.objectionId!, new Set());
        objectionIdAndMessageList.set(sessionMessage.subData.objectionId!, []);
      }

      objectionIdAndMemberSet.get(sessionMessage.subData.objectionId!)!.add(sessionMessage.createdBy);
      objectionIdAndMessageList.get(sessionMessage.subData.objectionId!)!.push(sessionMessage);

      sessionIdSet.add(sessionMessage.parentId);
    });

    const objectionIdAndAverageScore = new Map<string, number>();

    objectionIdAndMessageList.forEach((messageList, objectionId) => {
      const totalScore = messageList.reduce((prev, curr) => prev + curr.subData.objectionScore!, 0);
      const averageScore = totalScore / messageList.length;

      objectionIdAndAverageScore.set(objectionId, averageScore);
    });

    const objectionList = filteredSessionMessageList
      .filter((c) => c.subData.objectionId)
      .map((c) => {
        return this.allObjectionList().find((objection) => objection.id === c.subData.objectionId)!;
      })
      .filter((objection) => {
        return objection.type === objectionType;
      });

    return objectionList.map((objection) => ({
      objection,
      memberList: Array.from(objectionIdAndMemberSet.get(objection.id)!),
      averageScore: objectionIdAndAverageScore.get(objection.id)!,
      sessionIdList: Array.from(sessionIdSet),
    }));
  }
}
